//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import classNames from 'classnames';
import React from 'react';

import { ThemeProps, themeModifier } from '../helpers/themedSurface';

// Re-export the shared theme types so `import { UtilityVariant } from '.../RCThemedBox'` keeps working.
export type {
  UtilityColor,
  UtilityVariant,
  SurfaceVariant,
  TempoTheme,
} from '../helpers/themedSurface';

// Matches RCButton: optional props use destructuring defaults (not defaultProps),
// and `...rest` forwards HTML attributes to the underlying element.
/* eslint-disable react/require-default-props, react/jsx-props-no-spreading */

export type RCThemedBoxProps = React.ComponentPropsWithoutRef<'div'> & {
  /** Border style. `thin` is a full hairline border; `leading-accent` is a thick leading bar. Defaults to `none`. */
  border?: 'none' | 'thin' | 'leading-accent';
} & ThemeProps;

/**
 * Utility component — a themed surface via the `.themed-box` UI class; forwards
 * `className` + HTML attributes. No structural contract. To color a nested icon to the
 * surface, use `<RCIcon color="accent-themed" />` (it reads the same `--_theme-accent` channel).
 */
// The box owns only its decoration (theme colors, border). All sizing — padding,
// display, margin, border-radius, overflow — is left to utility classes via
// `className` (including their responsive variants). The box emits the UI-layer
// `.themed-box` class (shared with non-component DOM), so those utilities — loaded
// after the UI layer — compose and override it by normal cascade, no specificity
// juggling (see the cadence cascade convention).
const RCThemedBoxComponent = React.forwardRef<HTMLDivElement, RCThemedBoxProps>(
  function RCThemedBox({
    themeType = undefined,
    variant = undefined,
    border = 'none',
    className,
    children,
    ...rest
  }, ref) {
    return (
      <div
        ref={ref}
        className={classNames(
          'themed-box',
          themeModifier('themed-box', themeType, variant),
          { [`themed-box--border-${border}`]: border !== 'none' },
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

export const RCThemedBox = RCThemedBoxComponent;
